import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { RecipeTranslationVariant } from '@shared/models/recipe/recipes-translation-form.model';
import { RecipesTranslationFieldsContainerComponent } from '@recipes/recipes-translation/recipes-translation-fields-container/recipes-translation-fields-container.component';
import {
  RecipesTranslationSectionComponent,
  TranslationLang,
} from '@recipes/recipes-translation/recipes-translation-section/recipes-translation-section.component';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { IngredientType } from '@shared/models/dataref.model';
import { RefDataService } from '@services/ref-data.service';
import { Observable, of } from 'rxjs';
import { TranslateReferenceCodePipe } from '@shared/pipes/translate-reference-code.pipe';
import { TranslateModule } from '@ngx-translate/core';
import {
  FieldsOptions,
  RecipesTranslationInputFieldsComponent,
} from '@recipes/recipes-translation/recipes-translation-input-fields/recipes-translation-input-fields.component';
import { MatDividerModule } from '@angular/material/divider';
import { RecipesTranslationFieldsHeaderComponent } from '@recipes/recipes-translation/recipes-translation-fields-header/recipes-translation-fields-header.component';
import { RecipesTranslationVariantsComponent } from '@recipes/recipes-translation/recipes-translation-variants/recipes-translation-variants.component';

type IngredientFields = {
  variants: string[];
} & Pick<FieldsOptions, 'targetControl' | 'sourceControl'>;

interface IngredientGroup {
  type: string;
  fields: IngredientFields[][];
}

@Component({
  selector: 'app-recipes-translation-ingredients',
  templateUrl: './recipes-translation-ingredients.component.html',
  styleUrls: ['./recipes-translation-ingredients.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RecipesTranslationFieldsContainerComponent,
    RecipesTranslationSectionComponent,
    NgForOf,
    TranslateReferenceCodePipe,
    AsyncPipe,
    TranslateModule,
    NgIf,
    MatDividerModule,
    RecipesTranslationFieldsHeaderComponent,
    RecipesTranslationInputFieldsComponent,
    RecipesTranslationVariantsComponent,
  ],
})
export class RecipesTranslationIngredientsComponent implements OnInit {
  @Input() source: RecipeTranslationVariant[];
  @Input() target: RecipeTranslationVariant[];
  @Input() market: string;
  @Input() lang: string;
  @Input() tradLang: TranslationLang;

  fallbackMarket = 'GS_INT';
  fallbackLang = 'en';

  ingredientTypes$: Observable<IngredientType[]>;

  ingredientsByType: Record<string, IngredientFields[][]> = {};
  sortedIngredientsByType: IngredientGroup[] = [];

  private readonly refDataService = inject(RefDataService);

  ngOnInit(): void {
    this.ingredientTypes$ = this.refDataService.getByKey('ingredientTypes', false).pipe(
      map((ingredientTypes) => this.refDataService.toArray(ingredientTypes)),
      switchMap((ingredientTypesArray) => {
        const translatedIngredientTypes = this.refDataService.translateReference(ingredientTypesArray, this.market, this.lang);

        if (!translatedIngredientTypes || translatedIngredientTypes.length === 0) {
          const translatedIngTypesFallback = this.refDataService.translateReference(
            ingredientTypesArray,
            this.fallbackMarket,
            this.fallbackLang,
          );
          return of(translatedIngTypesFallback);
        }

        return of(translatedIngredientTypes);
      }),
      shareReplay(1),
    );

    this.mapRecipeToField();
  }

  /**
   * Maps the source and target recipe ingredients into a structured format grouped by ingredient type.
   *
   * This method iterates over the source recipes and their corresponding target recipes to extract
   * ingredient details. For each ingredient type, it collects pairs of source and target ingredient
   * fields, associating them with their recipe quantity as a variant.
   *
   * The collected data is stored in the `ingredientsByType` property as a record where keys are
   * ingredient types and values are arrays of arrays of `Fields` objects representing each ingredient.
   * After mapping, it triggers sorting and transformation of this data for easier iteration and display.
   */
  private mapRecipeToField(): void {
    this.source.forEach((sourceRecipe, i) => {
      const sourceIngredients = sourceRecipe.form.controls.ingredients.controls;
      const targetIngredients = this.target[i].form.controls.ingredients.controls;

      Object.keys(sourceIngredients).forEach((type, j) => {
        const sourceArray = sourceIngredients[type].controls;
        const targetArray = Object.values(targetIngredients)[j].controls;

        sourceArray.forEach((ingredient, k) => {
          const valueEntry = Object.values(ingredient.controls)[0];
          const valueTarget = Object.values(targetArray[k].controls)[0];

          const fields: IngredientFields = {
            variants: [String(sourceRecipe.quantity)],
            sourceControl: {
              left: valueEntry.controls.description,
              right: valueEntry.controls.ingredient,
            },
            targetControl: {
              left: valueTarget.controls.description,
              right: valueTarget.controls.ingredient,
            },
          };

          this.ingredientsByType[type] = this.ingredientsByType[type] || [];
          this.ingredientsByType[type][k] = this.ingredientsByType[type][k] || [];
          this.ingredientsByType[type][k].push(fields);
        });
      });
    });
    this.prepareSortedIngredientsEntries();
  }

  /**
   * Converts the `ingredientsByType` record into an iterable array of entries
   * and sorts each group of ingredient fields by their variant number in ascending order.
   *
   * This prepares the data for easier iteration and display
   */
  private prepareSortedIngredientsEntries(): void {
    this.sortedIngredientsByType = Object.entries(this.ingredientsByType).map(([type, fields]) => {
      fields.forEach((fieldGroup) => {
        fieldGroup.sort((a, b) => parseInt(a.variants[0], 10) - parseInt(b.variants[0], 10));
      });
      return { type, fields };
    });
  }
}
